import { parsedFrontendConfig } from 'frontend/shared/javascripts/util/frontend_config'
import { postNativeMessage } from './messages'

export interface NativeAppNavigation {
  /** Go to an in-app path. */
  visit(path: string): void

  /** The path currently displayed, as the frontend's own history sees it. */
  currentLocation(): string

  /** Close an open modal. `true` means the back gesture was consumed by closing it. */
  dismissOverlay(): boolean
}

const ROOT_SECTION = 'topics'

// The page each tab last showed outlives a full page load, such as one across the boundary to the Inertia app.
const STORAGE_KEY = 'studyflix:native-app:section-routes'

export class NativeAppRouter {
  private routePerSection: Record<string, string>
  private currentSection: string | null = null

  constructor(private navigation: NativeAppNavigation) {
    this.routePerSection = {
      ...((parsedFrontendConfig('routes') as Record<string, string> | null) ?? {}),
      ...storedRoutes(),
    }
  }

  /** Handle a tap on the shell's tab bar. Tapping the active root tab again goes home. */
  goTo(section: string): void {
    let path: string | undefined
    if (section !== this.currentSection) {
      path = this.routePerSection[section]
    } else if (this.onRootSection()) {
      path = this.routePerSection.home
    }

    if (path) {
      this.navigation.dismissOverlay()
      this.navigation.visit(path)
    }
  }

  goToRootSection(): void {
    this.goTo(ROOT_SECTION)
  }

  /** Tell the shell which tab is active, or `null` to clear the selection. */
  highlight(section: string | null): void {
    if (section) {
      postNativeMessage({ command: 'setActiveNavigationBarItem', key: section })
      this.routePerSection[section] = this.navigation.currentLocation()
      storeRoutes(this.routePerSection)
    } else {
      postNativeMessage('resetNavigationBar')
    }
    this.currentSection = section
  }

  onRootSection(): boolean {
    return this.currentSection === ROOT_SECTION
  }
}

function storedRoutes(): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? '{}')
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {}
  } catch {
    return {}
  }
}

function storeRoutes(routes: Record<string, string>): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(routes))
  } catch {
    // Without storage, a full page load sends the tabs back to their defaults.
  }
}
